/**
 * Regions of interest — toggle the payment regions the merchant cares
 * about. Used by sales/AM to target product pitches and to filter the
 * pipeline view.
 *
 * Allowlist comes from the server (`GET /regions`) so the frontend
 * doesn't have to know the canonical list — keeps the two in sync as
 * we add regions over time.
 *
 * UX: pill toggles. Auto-saves the whole list on every toggle (PUT
 * replaces the array). Optimistic — reverts if the server rejects.
 */
import React, { useEffect, useState } from "react";
import { Check, Globe2, Loader2 } from "lucide-react";
import api from "../../../lib/api";

export default function MerchantRegions({ merchantId }) {
  const [allowed, setAllowed] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingRegion, setSavingRegion] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/admin/merchants/${merchantId}/regions`);
        if (!alive) return;
        setAllowed(data.allowed || []);
        setSelected(data.selected || []);
        setUpdatedAt(data.updated_at);
        setUpdatedBy(data.updated_by);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.detail || "Couldn't load regions.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [merchantId]);

  const toggle = async (region) => {
    if (savingRegion) return;
    const wasSelected = selected.includes(region);
    const next = wasSelected
      ? selected.filter((r) => r !== region)
      : [...selected, region];
    // Canonical order from `allowed`
    const canonical = allowed.filter((r) => next.includes(r));
    const previous = selected;
    setSelected(canonical);
    setSavingRegion(region);
    setError("");
    try {
      const { data } = await api.put(
        `/admin/merchants/${merchantId}/regions`,
        { regions: canonical },
      );
      setSelected(data.selected || canonical);
      if (data.updated_at) setUpdatedAt(data.updated_at);
      if (data.updated_by) setUpdatedBy(data.updated_by);
    } catch (e) {
      // Roll back optimistic state on server reject.
      setSelected(previous);
      setError(e?.response?.data?.detail || "Couldn't save.");
    } finally { setSavingRegion(null); }
  };

  return (
    <section
      data-testid="merchant-regions"
      className="rounded-2xl border border-white/[0.06] bg-white/[0.01] p-5 sm:p-6"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Globe2 className="h-3.5 w-3.5 text-zinc-500" />
          <span className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Regions of interest</span>
        </div>
        {updatedAt && (
          <div className="text-[10.5px] text-zinc-600">
            updated {new Date(updatedAt).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
            {updatedBy ? ` · ${updatedBy.split("@")[0]}` : ""}
          </div>
        )}
      </div>

      <p className="mt-2 text-[12px] text-zinc-500 leading-relaxed">
        Mark every region the merchant wants to process in. The pipeline view filters by these.
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        {loading ? (
          <div className="text-[12px] text-zinc-500"><Loader2 className="h-3 w-3 animate-spin inline mr-1.5" />Loading regions…</div>
        ) : allowed.map((r) => {
          const isOn = selected.includes(r);
          const isSaving = savingRegion === r;
          return (
            <button
              key={r}
              type="button"
              data-testid={`merchant-region-toggle-${r.replace(/\W+/g, "-").toLowerCase()}`}
              data-selected={isOn ? "true" : "false"}
              onClick={() => toggle(r)}
              disabled={!!savingRegion}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] transition-colors disabled:cursor-wait ${
                isOn
                  ? "border-cyan-400/45 bg-cyan-400/[0.10] text-cyan-100"
                  : "border-white/[0.08] bg-white/[0.02] text-zinc-300 hover:border-white/30 hover:bg-white/[0.05]"
              }`}
            >
              {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : isOn ? <Check className="h-3 w-3" /> : null}
              {r}
            </button>
          );
        })}
      </div>

      {error && (
        <div data-testid="merchant-regions-error" className="mt-2 text-[11.5px] text-rose-300">{error}</div>
      )}
    </section>
  );
}
